import React from "react";
import useStore from "@/store/store";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/toast/CustomToast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import type { CurDate } from "@store/scheduleStore.ts";
import { useIsAdmin } from "@/hooks/useAuth";

export type RosterViewSettingsProps = {
  /** 전체보기(0) / 팀별보기(1) */
  currentTeamTableViewTab: number;
  setCurrentTeamTableViewTab: React.Dispatch<React.SetStateAction<number>>;
  /** 원티드 반영사항 로딩 중이면 토글·뷰 전환 비활성화 (ButtonGroup과 동일 정책) */
  isWantedAdjustmentLoading: boolean;
  /** 전월 말일 이어보기 */
  isPrevMonthOpen: boolean;
  setIsPrevMonthOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isFetchingPrevTail: boolean;
  curDate: CurDate | null;
  /** 인력 제약 검증을 끄는 화면이면 최소인원 스위치 숨김 (RosterMadeTable과 동일) */
  hideManpowerViolationToggle?: boolean;
};

function SettingSwitchRow({
  label,
  checked,
  onCheckedChange,
  disabled,
  tooltip,
  busy,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  tooltip?: string;
  busy?: boolean;
}) {
  const row = (
    <div
      className={cn(
        "flex items-center justify-between gap-3 min-w-[200px] sm:min-w-0 sm:flex-1 sm:max-w-[220px]",
        disabled && !busy && "opacity-50"
      )}
    >
      <span className="text-sm text-zinc-700 whitespace-nowrap shrink-0">
        {label}
      </span>
      <div className="flex items-center gap-1.5 shrink-0">
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" aria-hidden />
        ) : null}
        <Switch
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled || busy}
          className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-zinc-300"
          aria-label={label}
        />
      </div>
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex">{row}</div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return row;
}

/**
 * 근무표 작성 화면 — 보기 설정 (가로 스위치 행).
 * 상호 배제는 store `violation-visibility-slice`와 동일:
 * - 원티드 ON → 설정·최소인원 OFF
 * - 설정 또는 최소인원 ON → 원티드 OFF
 */
function RosterViewSettings({
  currentTeamTableViewTab,
  setCurrentTeamTableViewTab,
  isWantedAdjustmentLoading,
  isPrevMonthOpen,
  setIsPrevMonthOpen,
  isFetchingPrevTail,
  curDate,
  hideManpowerViolationToggle = false,
}: RosterViewSettingsProps) {
  const isAdmin = useIsAdmin();

  const {
    showManpowerViolations,
    showConfigViolations,
    showWantedViolations,
    toggleManpowerViolations,
    toggleConfigViolations,
    toggleWantedViolations,
  } = useStore();

  const violationDisabled = isWantedAdjustmentLoading;
  const viewModeDisabled = isWantedAdjustmentLoading;
  const prevMonthDisabled =
    isWantedAdjustmentLoading || !curDate?.year || !curDate?.month;

  return (
    <section
      className="w-full flex justify-between items-center  rounded-lg border border-zinc-200 bg-[#FAF9F9] px-4 py-3 mb-3"
      aria-label="보기 설정"
    >
      <h2 className="text-sm font-semibold text-zinc-700 ">보기 설정</h2>

      <div
        className={cn(
          "flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-6 sm:gap-y-3"
        )}
      >
        {!isAdmin && (
          <SettingSwitchRow
            label="원티드 반영사항 보기"
            checked={showWantedViolations}
            onCheckedChange={(checked) => {
              if (checked !== showWantedViolations) toggleWantedViolations();
            }}
            disabled={violationDisabled}
          />
        )}

        <SettingSwitchRow
          label="설정 위반사항 보기"
          checked={showConfigViolations}
          onCheckedChange={(checked) => {
            if (checked !== showConfigViolations) toggleConfigViolations();
          }}
          disabled={violationDisabled}
        />

        {!hideManpowerViolationToggle ? (
          <SettingSwitchRow
            label="최소인원 위반사항 보기"
            checked={showManpowerViolations}
            onCheckedChange={(checked) => {
              if (checked !== showManpowerViolations)
                toggleManpowerViolations();
            }}
            disabled={violationDisabled}
          />
        ) : null}

        <SettingSwitchRow
          label="전월 근무 이어보기"
          checked={isPrevMonthOpen}
          onCheckedChange={(checked) => {
            if (!curDate?.year || !curDate?.month) {
              toast.error("날짜를 선택해주세요.");
              return;
            }
            setIsPrevMonthOpen(checked);
          }}
          disabled={prevMonthDisabled}
          busy={isFetchingPrevTail}
          tooltip="전월 말일 근무표 영역 표시"
        />

        <SettingSwitchRow
          label="전체보기 / 팀별보기"
          checked={currentTeamTableViewTab === 0}
          onCheckedChange={(checked) => {
            setCurrentTeamTableViewTab(checked ? 0 : 1);
          }}
          disabled={viewModeDisabled}
          tooltip="켜짐: 전체보기 · 꺼짐: 팀별보기"
        />
      </div>
    </section>
  );
}

export default React.memo(RosterViewSettings);
